// @flow
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import type { Connector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useScreenWidth } from '../../common';
// Selector
import {
  indexDepartmentsSelector,
  currentDepartmentsIndexLoadingSelector,
} from '../selectors';
import {
  loggedInSelector,
  validatingUserLoadingSelector,
  isUserValidatedSelector,
} from '../../user/selectors';
// Actions
import { findDepartmentsIndex } from '../actions';
// Analytics
import * as Analytics from '../../common/analytics';
// Types
import type { Department } from '../types';
// Styles
import './DepartmentsNavList.css';
// Components
import DepartmentsSubNav from '../components/DepartmentsSubNav';
import DepartmentsNavListSkeleton from './DepartmentsNavListSkeleton';

type Props = {
  departments: Department[],
  getDepartments: () => void,
};

const DepartmentsNavList = (props: Props) => {
  const { departments = [] } = props;
  const sliderRef = useRef(null);
  const screenWidth = useScreenWidth();
  const location = useLocation();

  const [hoveredDept, setHoveredDept] = useState(null);
  const [hoveredSub, setHoveredSub] = useState(null);

  const shouldShowArrows = departments.length > 11 || screenWidth < 1440;
  const onAccountPage = location.pathname.includes('/minha-conta');

  const shouldShowLoading =
    (props.loading && departments.length === 0) ||
    (props.validatingUserLoading && !props.isUserValidated);

  const handleMouseLeaveDepartmentPopup = () => {
    setHoveredDept(null);
    setHoveredSub(null);
  };

  const handleMouseEnterDepartment = (d: Department) => {
    if (!onAccountPage) {
      setHoveredDept(d);
      const subdepartmentsWithProducts = d.subdepartments.filter(
        s => s.products && s.products.length > 0,
      );
      const firstSub =
        subdepartmentsWithProducts && subdepartmentsWithProducts.length > 0
          ? subdepartmentsWithProducts[0]
          : null;
      setHoveredSub(firstSub);
    }
  };

  const handleScrollSlider = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const width = sliderRef.current.clientWidth || 400;
      sliderRef.current.scrollBy({
        left: direction === 'left' ? -width * 0.8 : width * 0.8,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (departments.length === 0) {
      props.getDepartments();
    }
  }, []);

  if (shouldShowLoading) {
    return <DepartmentsNavListSkeleton />;
  }

  return (
    <div onMouseLeave={handleMouseLeaveDepartmentPopup}>
      <div className="nav-list-bottom-carousel">
        {shouldShowArrows && (
          <button
            type="button"
            aria-label="previous departments"
            className="dept-nav-prev"
            onClick={() => handleScrollSlider('left')}
          >
            ‹
          </button>
        )}
        <div
          ref={sliderRef}
          className={`nav-list-bottom-main  manual-slider ${shouldShowArrows ? 'nav-list-bottom-main-scrollable' : ''}`}
        >
          {departments.slice(0, 2).map((d, ind) => (
            <div
              className={`dept-item manual-slider-item ${hoveredDept && hoveredDept.id === d.id ? 'hovered' : ''} `}
              key={ind}
              onMouseEnter={() => handleMouseEnterDepartment(d)}
            >
              <Link
                onClick={() => Analytics.logTappedNavList(d.name.toLowerCase())}
                to={`/departamentos/${d.id}/`}
              >
                <span className="first-name">
                  {d.name
                    ? d.name.charAt(0).toUpperCase() +
                      d.name.slice(1).toLowerCase()
                    : null}
                </span>
              </Link>
            </div>
          ))}
          <div
            className="manual-slider-extra"
            onMouseEnter={() => {
              setHoveredDept(null);
            }}
          >
            <Link
              onClick={() =>
                props.loggedIn
                  ? Analytics.logTappedNavList('meus blends')
                  : Analytics.logTappedNavList('blends')
              }
              to="/blend/"
            >
              <span className="first-name">
                {props.loggedIn ? 'Meus blends' : 'Blends'}
              </span>
            </Link>
          </div>
          <div
            className="manual-slider-extra"
            onMouseEnter={() => {
              setHoveredDept(null);
            }}
          >
            <Link
              onClick={() => Analytics.logTappedNavList('churrascometro')}
              to="/bigchurrasco/"
            >
              <span className="first-name">Calculadora para Churrasco</span>
            </Link>
          </div>
          {departments.slice(2, departments.length + 1).map((d, ind) => (
            <div
              className={`dept-item manual-slider-item ${hoveredDept && hoveredDept.id === d.id ? 'hovered' : ''}`}
              key={ind}
              onMouseEnter={() => handleMouseEnterDepartment(d)}
            >
              <Link
                onClick={() => Analytics.logTappedNavList(d.name.toLowerCase())}
                to={`/departamentos/${d.id}/`}
              >
                {d.name
                  ? d.name.charAt(0).toUpperCase() +
                    d.name.slice(1).toLowerCase()
                  : null}
              </Link>
            </div>
          ))}
        </div>

        {shouldShowArrows && (
          <button
            type="button"
            aria-label="next departments"
            className="dept-nav-next"
            onClick={() => handleScrollSlider('right')}
          >
            ›
          </button>
        )}
      </div>

      <DepartmentsSubNav
        hoveredDept={hoveredDept}
        hoveredSub={hoveredSub}
        setHoveredSub={setHoveredSub}
        setHoveredDept={setHoveredDept}
      />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    departments: indexDepartmentsSelector(state),
    loading: currentDepartmentsIndexLoadingSelector(state),
    isUserValidated: isUserValidatedSelector(state),
    validatingUserLoading: validatingUserLoadingSelector(state),
    loggedIn: loggedInSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps) => {
  return {
    getDepartments: () => {
      dispatch(findDepartmentsIndex());
    },
  };
};

const connector: Connector<{}, Props> = connect(
  mapStateToProps,
  mapDispatchToProps,
);
export default connector(DepartmentsNavList);
